import { getStore } from '../store/storeRef';
import { logout } from '../store/slices/authSlice';
import { STORAGE_KEYS } from '../utils/storageKeys';

// Build-time constant — Vite replaces this with the literal string value.
const AUTH_PROVIDER = import.meta.env.VITE_AUTH_PROVIDER || 'auth0';

/**
 * Provider-agnostic logout.
 * Called by appAuthService when a token refresh fails and the session expires.
 * For Auth0: redirects to Auth0 /v2/logout.
 * For Authentik: redirects to Authentik end_session_endpoint.
 */
export const performLogout = (): void => {
  // Capture the OIDC id_token BEFORE clearing storage — it is required as
  // id_token_hint on the Authentik end-session request so Authentik can end the
  // SSO session and redirect back to the app, instead of stranding the user on
  // its "Not Found" session-end page.
  let idToken = '';
  if (AUTH_PROVIDER === 'authentik') {
    const oidcKey = Object.keys(localStorage).find((key) =>
      key.startsWith('oidc.user:')
    );
    if (oidcKey) {
      try {
        idToken = JSON.parse(localStorage.getItem(oidcKey) || '{}').id_token || '';
      } catch {
        // Malformed store entry — proceed without the hint.
      }
    }
  }

  // Clear Redux state
  getStore().dispatch(logout());

  // Clear stored tokens
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);

  // Clear all auth-related localStorage keys
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('auth0.') || key.startsWith('oidc.')) {
      localStorage.removeItem(key);
    }
  });

  // Clear session storage
  sessionStorage.clear();

  if (AUTH_PROVIDER === 'authentik') {
    // RP-initiated logout via Authentik's OIDC end_session_endpoint.
    // Authority:            http://<host>/application/o/<slug>/
    // End-session endpoint: http://<host>/application/o/<slug>/end-session/
    // post_logout_redirect_uri must match a Redirect URI registered on the
    // provider, or Authentik ignores it and shows its session-end page.
    const authority = import.meta.env.VITE_AUTHENTIK_AUTHORITY || '';
    // '/admin' — this app is only ever served under that path (see
    // AuthentikProvider.tsx for the full basename/vite-base/ingress rationale).
    const postLogout =
      import.meta.env.VITE_AUTHENTIK_POST_LOGOUT_REDIRECT_URI ||
      `${window.location.origin}/admin/`;

    if (authority) {
      const params = new URLSearchParams({
        post_logout_redirect_uri: postLogout,
      });
      if (idToken) params.set('id_token_hint', idToken);
      window.location.href = `${authority}end-session/?${params.toString()}`;
    } else {
      window.location.href = '/admin/';
    }
  } else {
    // Set flag to prevent automatic re-login after logout
    sessionStorage.setItem(STORAGE_KEYS.AUTH0_LOGOUT_FORCED, 'true');
    const domain = import.meta.env.VITE_AUTH0_DOMAIN;
    const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
    if (domain && clientId) {
      const returnTo = encodeURIComponent(window.location.origin);
      window.location.href = `https://${domain}/v2/logout?client_id=${clientId}&returnTo=${returnTo}`;
    } else {
      window.location.href = '/';
    }
  }
};
